import classNames from 'classnames';
import { partition } from 'lodash-es';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import OneColumnPage from '../app/layout/OneColumnPage';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import GroupSquarishCard from '../components/GroupSquarishCard';
import SquarishCardGrid from '../components/SquarishCardGrid';
import Tooltip from '../components/Tooltip';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';
import { UIActions } from '../ui/uiSlice';
import AdminListButton from '../user/AdminListButton';
import { legacyUpdateQueryList } from '../utils/graphql/updateQueryList';
import GroupModal from './GroupModal';
import * as styles from './GroupsView.module.scss';
import OrganizationUsers from './OrganizationUsers';
import groupPolicyCheck, { GroupPolicyAction } from './groupPolicyCheck';

export const GROUPS_VIEW_QUERY = gql(/** @complexity 15 */ `
  query GroupsView {
    session: currentSession {
      organization {
        id
        settings {
          allowUserGroupCreation
        }
        groups {
          allUsers {
            id
          }
          ...GroupSquarishCard_group
        }
      }
    }
  }
`);

const GROUP_VIEW_CREATE_MUTATION = gql(/** @complexity 12 */ `
  mutation GroupViewCreate($name: String!, $ownerId: ID!) {
    group: createGroup(name: $name, ownerId: $ownerId) {
      allUsers {
        id
      }
      ...GroupSquarishCard_group
    }
  }
`);

const GroupsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading } = useQuery(GROUPS_VIEW_QUERY);

  const [createGroupMutation] = useMutation(GROUP_VIEW_CREATE_MUTATION);

  const isUserGroupCreationAllowed =
    !!data?.session.organization?.settings.allowUserGroupCreation;

  const groups = data?.session.organization?.groups ?? [];

  const currentUser = useAppSelector((state) => state.session.user);
  const isAdmin = currentUser?.type === UserType.Admin;
  const canCreate = isUserGroupCreationAllowed
    ? currentUser?.type !== UserType.Guest
    : isAdmin;

  const canRequest = groupPolicyCheck(
    GroupPolicyAction.RequestGroup,
    currentUser,
  );
  const dispatch = useAppDispatch();
  const showNewGroupModal = () => {
    dispatch(UIActions.openModal(GroupModal, { canCreate }));
  };

  useEffect(() => {
    const approveGroupCreation = searchParams.get('approveGroupCreation');
    const ownerId = searchParams.get('ownerId');
    if (isAdmin && approveGroupCreation && ownerId) {
      createGroupMutation({
        variables: {
          name: approveGroupCreation,
          ownerId,
        },
        update: legacyUpdateQueryList(
          { query: GROUPS_VIEW_QUERY },
          'session.organization.groups',
          'group',
          true,
        ),
      }).then(() => {
        setSearchParams({});
      });
    }
  }, [isAdmin, createGroupMutation, searchParams, setSearchParams]);

  const [myGroups, allGroups] = partition(groups, (group) =>
    group.allUsers.some((member) => member.id === currentUser?.id),
  );

  const createGroupButton = (
    <div>
      <Tooltip
        content={
          !canCreate && !canRequest
            ? `Groups can only be\ncreated by ${
                isUserGroupCreationAllowed ? 'users' : 'admins'
              }`
            : undefined
        }
      >
        <Button
          disabled={!canCreate && !canRequest}
          onClick={showNewGroupModal}
          variant="primary"
        >
          {!canCreate && canRequest ? 'Request group' : 'New group'}
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <PageLayout.Content noPaddingY noPaddingX>
      <OneColumnPage>
        {groups.length === 0 && !loading && (
          <>
            <div className={styles.header}>
              <h3>Groups</h3>
              {createGroupButton}
            </div>
            <EmptyState
              imageName="posts"
              title="No groups yet..."
              message={
                canCreate
                  ? 'Create a group to add members and topic access.'
                  : 'Ask an admin to create a group for your team.'
              }
              action={
                <>
                  {canCreate && (
                    <Button
                      size="large"
                      variant="secondary"
                      onClick={showNewGroupModal}
                    >
                      Create a group
                    </Button>
                  )}
                  {!canCreate && !canRequest && (
                    <AdminListButton size="large" variant="secondary">
                      See admins
                    </AdminListButton>
                  )}
                </>
              }
            />
          </>
        )}
        {myGroups.length > 0 && (
          <div className={styles.section}>
            <div className={styles.header}>
              <h3>My Groups</h3>
              {createGroupButton}
            </div>
            <SquarishCardGrid>
              {myGroups.map((group) => (
                <SquarishCardGrid.Item key={group.id}>
                  <GroupSquarishCard group={group} link />
                </SquarishCardGrid.Item>
              ))}
            </SquarishCardGrid>
          </div>
        )}
        {allGroups.length > 0 && (
          <div className={styles.section}>
            <div className={styles.header}>
              <h3>All Groups</h3>
              {myGroups.length === 0 && createGroupButton}
            </div>
            <SquarishCardGrid>
              {allGroups.map((group) => (
                <SquarishCardGrid.Item key={group.id}>
                  <GroupSquarishCard group={group} link />
                </SquarishCardGrid.Item>
              ))}
            </SquarishCardGrid>
          </div>
        )}
        <div
          className={classNames(styles.section, { [styles.hidden]: loading })}
        >
          <div className={styles.header}>
            <h3>All users</h3>
          </div>
          <OrganizationUsers />
        </div>
      </OneColumnPage>
    </PageLayout.Content>
  );
};

export default GroupsView;
